"use client";

import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { BarChart3, FileText, Users, TrendingUp, Download, Edit, Trash2 } from "lucide-react";
import { SubmissionEditor } from "./submission-editor";
import PropTypes from "prop-types";
import "./admin-dashboard.css";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import MDBox from "components/MDBox";
import MDAutocomplete from "components/MDAutocomplete";
import MDButton from "components/MDButton";
import {
  Box,
  Grid,
  Paper,
  Card as MUICard,
  Tooltip,
  Button as MUIButton,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";

export function AdminDashboard({
  forms = [],
  onEditForm,
  onDeleteForm,
  onUpdateSubmission,
  onViewFormReport,
}) {
  console.log("Admin Form:", forms);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [expandedForm, setExpandedForm] = useState(null);
  const [userSummary, setUserSummary] = useState(null);
  const [filters, setFilters] = useState({ form_name: null });
  const [filteredForms, setFilteredForms] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const totalForms = forms.length;
  const totalSubmissions = forms.reduce((acc, form) => acc + (form.submissions?.length || 0), 0);
  const avgSubmissionsPerForm = totalForms > 0 ? Math.round(totalSubmissions / totalForms) : 0;

  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    // console.log("🚀 ~ fetchUsers ~ response:", response);
    return response.data;
  };

  const {
    data: meData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["me"], // Unique key for the query
    queryFn: me, // The async function to run
    retry: false,
  });

  const allforms = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/forms/dropdown/all`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    // console.log("🚀 ~ fetchUsers ~ response:", response);
    return response.data.data;
  };

  const {
    data: formsData,
    formsLoading,
    formsError,
    formserror,
  } = useQuery({
    queryKey: ["allforms"], // Unique key for the query
    queryFn: allforms, // The async function to run
    retry: false,
  });

  console.log("FormsData:", formsData);
  useEffect(() => {
    if (!meData?.user_id) return;

    const fetchUserSummary = async () => {
      try {
        let res;
        if (meData.user_id === 1) {
          // Admin: fetch all users summary
          res = await axios.get(`${process.env.REACT_APP_API_URL}/allforms/form/allCount`, {
            withCredentials: true,
          });
        } else {
          // Regular user: fetch only their summary
          res = await axios.get(`${process.env.REACT_APP_API_URL}/allforms/form/${meData.user_id}`, {
            withCredentials: true,
          });
        }

        setUserSummary(res.data.data);
        console.log("User Summary:", res.data.data);
      } catch (err) {
        console.error("Error fetching user summary:", err);
      }
    };

    fetchUserSummary();
  }, [meData?.user_id]);

  const getFormSubmissionCount = (formId) => {
  if (!userSummary?.submissions_per_form) return 0;
  const formSummary = userSummary.submissions_per_form.find((f) => f.form_id === formId);
  return formSummary ? formSummary.total_submissions : 0; // ✅ updated
};

  const fetchFilteredData = async (filters) => {
    console.log("Filters on search:", filters);
    if (filters.form_name) {
      try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/allforms/${filters.form_name}`,
        { withCredentials: true }
      );
      setFilteredForms([res.data.data]); // show only the selected form
      setIsFiltered(true);
    } catch (err) {
      console.error("Error fetching form by ID:", err);
    }
    }
  };

  const clearFilters = () => {
    setFilters({ form_name: null }); // reset filter state
    setFilteredForms(forms.slice(0, 5)); // back to default 5 forms
    setIsFiltered(false);
  };
  useEffect(() => {
    if (!isFiltered) {
      setFilteredForms(forms.slice(0, 5));
    }
  }, [forms, isFiltered]);
  const recentSubmissions = forms
    .flatMap((form) =>
      (form.submissions || []).map((submission) => ({
        ...submission,
        formTitle: form.title,
        formId: form.id,
        submittedAt: submission.created_at,
      }))
    )
    .sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime())
    .slice(0, 5);

  const exportFormData = (form) => {
    const dataStr = JSON.stringify(form, null, 2);
    const dataUri = "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);

    const exportFileDefaultName = `${form.title.replace(/\s+/g, "_")}_data.json`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();
  };

  const handleEditSubmission = (form, submission, index) => {
    setEditingSubmission({ form, submission, index });
  };

  const handleSaveSubmission = (updatedSubmission) => {
    if (editingSubmission) {
      onUpdateSubmission(editingSubmission.form.id, editingSubmission.index, updatedSubmission);
      setEditingSubmission(null);
    }
  };

  const toggleFormExpansion = (formId) => {
    setExpandedForm(expandedForm === formId ? null : formId);
  };

  if (editingSubmission) {
    return (
      <SubmissionEditor
        form={editingSubmission.form}
        submission={editingSubmission.submission}
        onSave={handleSaveSubmission}
        onCancel={() => setEditingSubmission(null)}
      />
    );
  }

  return (
    <div>
      {/* Stats Cards */}
      <div className="stats-grid">
        <Card className="stats-card" style={{ backgroundColor: "#35bbcab3", color: "white" }}>
          <CardHeader className="stats-card-header-row">
            <CardTitle className="stats-card-title">Total Forms</CardTitle>
            <FileText className="h-4 w-4 text-white" />
          </CardHeader>
          <CardContent>
            <div className="stats-card-value">{userSummary?.total_forms || 0}</div>
            <p className="stats-card-description">Active forms in your account</p>
          </CardContent>
        </Card>

        <Card className="stats-card" style={{ backgroundColor: "#5fa55ab3", color: "white" }}>
          <CardHeader className="stats-card-header-row">
            <CardTitle className="stats-card-title">Total Submissions</CardTitle>
            <Users className="h-4 w-4 text-white" />
          </CardHeader>
          <CardContent>
            <div className="stats-card-value">{userSummary?.total_submissions || 0}</div>
            <p className="stats-card-description">Responses collected</p>
          </CardContent>
        </Card>

        {/* <Card className="stats-card" style={{ backgroundColor: "#ec3432b3", color: "white" }}>
          <CardHeader className="stats-card-header-row">
            <CardTitle className="stats-card-title">Avg. per Form</CardTitle>
            <TrendingUp className="h-4 w-4 text-white" />
          </CardHeader>
          <CardContent>
            <div className="stats-card-value">{avgSubmissionsPerForm}</div>
            <p className="stats-card-description">Average submissions per form</p>
          </CardContent>
        </Card> */}

        <Card className="stats-card" style={{ backgroundColor: "#ff8a25b3", color: "white" }}>
          <CardHeader>
            <CardTitle className="stats-card-title">Active Today</CardTitle>
            <CardDescription>Submissions from today</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="stats-card-value">{userSummary?.today_submissions || 0}</div>
            <p className="stats-card-description">Submissions today</p>
          </CardContent>
        </Card>
      </div>

      <MDBox mt={3} />
      <MUICard
        id="delete-account"
        sx={{
          mb: 3,
          border: "2px solid #2bc29c", // green border
          borderRadius: "10px", // optional, for rounded corners
        }}
      >
        <MDBox px={2} pt={0.5}>
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, md: 12 }}>
              <MDAutocomplete
                label="Forms"
                name="form_name"
                value={filters.form_name}
                options={formsData}
                getOptionLabel={(option) => option.title || ""}
                onChange={(newValue) => {
                  setFilters({ ...filters, form_name: newValue });
                }}
                isFilter={true} // optional: add a flag if you need special styling
              />
            </Grid>
          </Grid>
        </MDBox>
        <MDBox p={2}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                onClick={() => fetchFilteredData(filters)}
                sx={{
                  color: "white !important",
                  backgroundColor: "#1976d2",
                  "&:hover": { backgroundColor: "#155fa0" },
                  minWidth: "120px",
                }}
              >
                Search
              </MDButton>
            </Grid>
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                variant="outlined"
                onClick={clearFilters}
                sx={{
                  color: "#b71c1c !important",
                  borderColor: "#b71c1c",
                  "&:hover": {
                    backgroundColor: "#fbe9e7",
                    borderColor: "#b71c1c",
                  },
                  minWidth: "120px",
                }}
              >
                Cancel
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </MUICard>

      {/* Forms List with Enhanced Analytics */}
      <Card className="section-card">
        <CardHeader className="section-header">
          <CardTitle className="section-title ">Form Management</CardTitle>
          <CardDescription className="section-description ">
            Manage your forms and view detailed analytics
          </CardDescription>

          {/* Updated description */}
        </CardHeader>
        <CardContent className="section-content">
          {filteredForms.length === 0 ? (
            <div className="activity-item">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="activity-item-title">No forms available for analytics</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredForms.map((form) => (
                <div key={form.id} className="form-card">
                  <div className="form-header bg-green-600 text-white p-4 rounded-lg flex justify-between items-center">
                    <div className="form-info">
                      <h4 className="form-title">{form.title}</h4>
                      <p className="form-description">{form.description}</p>
                      <p className="form-meta">
                        {form.fields?.length || 0} fields • Created{" "}
                        {new Date(form.createdAt).toLocaleDateString()}
                        {form.updatedAt && form.updatedAt !== form.createdAt && (
                          <span> • Updated {new Date(form.updatedAt).toLocaleDateString()}</span>
                        )}
                      </p>
                    </div>

                    <div className="form-actions flex items-center gap-3">
                      <div className="form-stats text-center">
                        <div className="form-stats-value text-lg font-bold">
                          {getFormSubmissionCount(form.id)}
                        </div>
                        <div className="form-stats-label text-sm">Submissions</div>
                      </div>

                      <div className="form-buttons flex gap-2">
                        {meData?.user_id === 1 && (
                          <Button
                            className="btn btn-outline btn-sm border-white text-white hover:bg-white hover:text-green-600"
                            variant="outline"
                            size="sm"
                            onClick={() => onEditForm(form.id)}
                          >
                            <Edit className="icon" />
                          </Button>
                        )}

                        <Button
                          className="btn btn-outline btn-sm border-white text-white hover:bg-white hover:text-green-600"
                          variant="outline"
                          size="sm"
                          onClick={() => onViewFormReport(form.id)}
                        >
                          <BarChart3 className="icon" />
                        </Button>

                        {meData?.user_id === 1 && (
                          <Button
                            className="btn btn-outline btn-sm border-white text-white hover:bg-white hover:text-green-600"
                            variant="outline"
                            size="sm"
                            onClick={() => onDeleteForm(form.id, form.title)}
                          >
                            <Trash2 className="icon" />
                          </Button>
                        )}
                      </div>

                      <Badge
                        className="btn btn-sm text-white border-none"
                        style={{ backgroundColor: "#2bc29c", color: "white" }} // green background + white text
                      >
                        {form.submissions?.length > 0 ? "Active" : "No responses"}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}

              {!isFiltered && forms.length > 5 && (
                <div className="text-center mt-4">
                  <span className="text-sm text-gray-600">+{forms.length - 5} more forms</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

AdminDashboard.propTypes = {
  forms: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      createdAt: PropTypes.string.isRequired,
      updatedAt: PropTypes.string,
      fields: PropTypes.array,
      submissions: PropTypes.arrayOf(
        PropTypes.shape({
          submittedAt: PropTypes.string.isRequired,
          updatedAt: PropTypes.string,
        })
      ),
    })
  ),
  onEditForm: PropTypes.func.isRequired,
  onDeleteForm: PropTypes.func.isRequired,
  onUpdateSubmission: PropTypes.func.isRequired,
  onViewFormReport: PropTypes.func.isRequired,
};
